import { mergeOptions } from '../../helpers/common/merge-options';

declare let ScrollMagic: any;
declare let TweenMax: any;
declare let Power2: any;
export const ScrollMagicController = new ScrollMagic.Controller();

export interface TweenEffectOptions {
	hook: number;
	offset: number;
	delay: number;
	duration: number;
	stagger: number;
	clipPercentage: number,
	individualScenes: boolean;
	easeFunc: any;
	targetPrefix: string;
}

export class TweenEffect {
	static options: TweenEffectOptions = {
		hook: .5,
		offset: 0,
		delay: 0,
		duration: .6,
		stagger: 0,
		clipPercentage: 10,
		individualScenes: false,
		easeFunc: Power2.easeInOut,
		targetPrefix: 'js-tween-effect'
	}

	private options: TweenEffectOptions;
	static clipFull = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';

	constructor(private element: HTMLElement, options?: Partial<TweenEffectOptions>) {
		this.options = mergeOptions(TweenEffect, options, element);

		this.initClipTop();
		this.initClipTopX();
		this.initClipTopLeft();
		this.initClipTopRight();
		this.initClipBottomLeft();
		this.initClipBottomRight();
		this.initScaleUp();
		this.initFadeIn();
		this.initFadeOut();
		this.initSlideVertical();
		this.initSlideHorizontal();
	}

	private initClipTopLeft() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-tl');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(${val}% ${val}%, 100% ${val}%, 100% 100%, ${val}% 100%)` }, { clipPath: TweenEffect.clipFull }, 'clip-tl');
		}
	}

	private initClipTop() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-t');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(0 ${val}%, 100% ${val}%, 100% 100%, 0% 100%)` }, { clipPath: TweenEffect.clipFull }, 'clip-t');
		}
	}

	private initClipTopX() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-tx');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(${val}% ${val}%, ${100 - val}% ${val}%, ${100 - val}% 100%, ${val}% 100%)` }, { clipPath: TweenEffect.clipFull }, 'clip-tx');
		}
	}

	private initClipTopRight() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-tr');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(0% ${val}%, ${100 - val}% ${val}%, ${100 - val}% 100%, 0% 100%)` }, { clipPath: TweenEffect.clipFull }, 'clip-tr');
		}
	}

	private initClipBottomLeft() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-bl');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(${val}% 0%, 100% 0%, 100% ${100 - val}%, ${val}% ${100 - val}%)` }, { clipPath: TweenEffect.clipFull }, 'clip-bl');
		}
	}

	private initClipBottomRight() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-clip-br');
		const val = this.options.clipPercentage;

		if (targets.length > 0) {
			this.createTween(targets, { clipPath: `polygon(0% 0%, ${100 - val}% 0%, ${100 - val}% ${100 - val}%, 0% ${100 - val}%)` }, { clipPath: TweenEffect.clipFull }, 'clip-br');
		}
	}

	private initScaleUp() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-scale-up');

		if (targets.length > 0) {
			this.createTween(targets, { scale: 0.9 }, { scale: 1 }, 'scale');
		}
	}

	private initFadeIn() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-fade-in');

		if (targets.length > 0) {
			this.createTween(targets, { opacity: 0 }, { opacity: 1 }, 'fade-in');
		}
	}

	private initFadeOut() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-fade-out');

		if (targets.length > 0) {
			this.createTween(targets, { opacity: 1 }, { opacity: 0 }, 'fade-out');
		}
	}

	private initSlideHorizontal() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-h-slide');

		if (targets.length > 0) {
			const val = parseFloat(this.element.getAttribute('data-tween-effect-h-slide') || '') || 50;
			this.createTween(targets, { translateX: val }, { translateX: 0 }, 'h-slide');
		}
	}

	private initSlideVertical() {
		const targets = this.element.querySelectorAll('.' + this.options.targetPrefix + '-v-slide');

		if (targets.length > 0) {
			const val = parseFloat(this.element.getAttribute('data-tween-effect-v-slide') || '') || 50;
			this.createTween(targets, { translateY: val }, { translateY: 0, opacity: 1 }, 'v-slide');
		}
	}

	private createTween(targets: NodeListOf<Element>, from: any, to: any, effect = '') {
		if (this.options.individualScenes) {
			targets.forEach(item =>
				new ScrollMagic.Scene({
					triggerElement: item,
					triggerHook: this.options.hook,
					offset: this.options.offset,
				})
					.setTween(TweenMax.fromTo(
						item,
						this.options.duration,
						{ css: from },
						{
							css: to,
							ease: this.options.easeFunc,
							stagger: this.options.stagger,
							delay: parseFloat(this.element.getAttribute('data-tween-effect-' + effect + '-delay') || '') || this.options.delay
						},
					))
					.addTo(ScrollMagicController)
				// .addIndicators({
				// 	name: effect,
				// })
			)
		} else {
			new ScrollMagic.Scene({
				triggerElement: this.element,
				triggerHook: this.options.hook,
				offset: this.options.offset,
			})
				.setTween(
					TweenMax.fromTo(
						targets,
						this.options.duration,
						{ css: from },
						{
							css: to,
							ease: this.options.easeFunc,
							stagger: this.options.stagger,
							delay: parseFloat(this.element.getAttribute('data-tween-effect-' + effect + '-delay') || '') || this.options.delay
						},
					))
				.addTo(ScrollMagicController)
			// .addIndicators({
			// 	name: effect,
			// })
		}
	}
}
